import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Circle, Popup, useMap } from "react-leaflet";
import { Leaf, Flower, Info, MapPin, ShieldCheck, Camera, ChevronDown, HelpCircle, X as XIcon } from "lucide-react";

import "leaflet/dist/leaflet.css";

import AppShell from "@/components/AppShell";
import PublishMoreCard from "@/components/corridor/PublishMoreCard";
import { corridor as corridorApi } from "@/lib/corridorApi";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Corridor — anonymous native-plant map for signed-in users in the same state.
 *
 * Two view modes driven by zoom:
 *  • zoom < 9  → heatmap (Circle radius scaled by count, ~ln-based so busy
 *    buckets don't visually dominate). No individual pins yet.
 *  • zoom ≥ 9 → individual anonymous pins (CircleMarker with tap-to-open
 *    popup showing plant name + native/keystone badges).
 *
 * Privacy invariants (mirror the backend):
 *  • Coordinates rendered are already server-bucketed (~5.5km grid). This
 *    file never touches raw GPS.
 *  • The pins carry NO user identity — plant_name + is_native + is_keystone
 *    only. If a maintainer ever adds fields here, cross-check the router
 *    projection first.
 *
 * A11y invariants:
 *  • Leaflet CircleMarkers are pure SVG paths and are not keyboard-focusable
 *    by default. `focusablePinHandlers` decorates each marker with tabindex,
 *    role=button and an aria-label, and wires Enter/Space to open its popup.
 *  • A visually-hidden <ul> below the map lists every pin as text — the
 *    non-visual equivalent of the pin cloud (WCAG 1.1.1).
 *  • The MapContainer div carries role=application + aria-label so a screen
 *    reader announces the region when it receives keyboard focus.
 */
const HEATMAP_MAX_ZOOM = 9;
const STATE_DEFAULT_VIEW = { center: [40.0, -89.0], zoom: 7 }; // Illinois-ish, replaced when pins load

function ZoomWatcher({ onZoom }) {
  const map = useMap();
  useEffect(() => {
    onZoom(map.getZoom());
    const handler = () => onZoom(map.getZoom());
    map.on("zoomend", handler);
    return () => {
      map.off("zoomend", handler);
    };
  }, [map, onZoom]);
  return null;
}

/**
 * MapA11y — decorates the runtime .leaflet-container (which react-leaflet
 * creates imperatively and does NOT forward arbitrary props to) with
 * role='application' + aria-label. Leaflet already sets tabindex=0 on the
 * container; we just add the missing name & role so screen readers
 * announce the map region when it receives keyboard focus (WCAG 4.1.2).
 */
function MapA11y({ ariaLabel }) {
  const map = useMap();
  useEffect(() => {
    const el = map.getContainer();
    if (!el) return;
    el.setAttribute("role", "application");
    if (ariaLabel) el.setAttribute("aria-label", ariaLabel);
  }, [map, ariaLabel]);
  return null;
}

function FitBoundsOnce({ pins }) {
  const map = useMap();
  const fitted = React.useRef(false);
  useEffect(() => {
    if (fitted.current || pins.length === 0) return;
    fitted.current = true;
    // Compute bounds from pin extent. Add a small padding so markers on the
    // edge aren't clipped by the tile pane.
    const lats = pins.map((p) => p.lat);
    const lons = pins.map((p) => p.lon);
    const min = [Math.min(...lats), Math.min(...lons)];
    const max = [Math.max(...lats), Math.max(...lons)];
    map.fitBounds([min, max], { padding: [40, 40], maxZoom: 10 });
  }, [map, pins]);
  return null;
}

/**
 * Build the react-leaflet `eventHandlers` object that makes a CircleMarker
 * keyboard-reachable and screen-reader-nameable. We add tabindex, role, and
 * an aria-label to the underlying SVG path on `add`, and open the popup on
 * Enter/Space so the pin behaves like a real button (WCAG 2.1.1, 4.1.2).
 */
function focusablePinHandlers(pin, ariaLabel) {
  return {
    add: (e) => {
      const el = e.target.getElement && e.target.getElement();
      if (!el) return;
      el.setAttribute("tabindex", "0");
      el.setAttribute("role", "button");
      el.setAttribute("aria-label", ariaLabel);
    },
    keydown: (e) => {
      const key = e.originalEvent?.key;
      if (key === "Enter" || key === " " || key === "Spacebar") {
        e.originalEvent.preventDefault();
        e.target.openPopup();
      }
    },
  };
}

export default function Corridor() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [zoom, setZoom] = useState(STATE_DEFAULT_VIEW.zoom);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // Legend collapse state, remembered per-user so power users only see
  // the legend as long as they want to. Keyed by user_id so a shared
  // browser doesn't leak one member's preference to another. Default:
  // expanded (safer for first-time visitors than silently collapsed).
  const legendKey = user?.user_id
    ? `prairie:corridor-legend-open:${user.user_id}`
    : "prairie:corridor-legend-open";
  const [legendOpen, setLegendOpen] = useState(true);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(legendKey);
      if (raw === "0") setLegendOpen(false);
      else if (raw === "1") setLegendOpen(true);
    } catch { /* private mode — leave default */ }
  }, [legendKey]);
  const toggleLegend = () => {
    setLegendOpen((open) => {
      const next = !open;
      try { localStorage.setItem(legendKey, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: body } = await corridorApi.list();
        if (!cancelled) setData(body);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || t("corridor.loadError"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const pins = data?.pins || [];
  const showPins = zoom >= HEATMAP_MAX_ZOOM;
  const totals = useMemo(() => {
    const nativeCount = pins.filter((p) => p.is_native).length;
    return { total: pins.length, natives: nativeCount, keystones: pins.filter((p) => p.is_keystone).length };
  }, [pins]);

  // Filter chip state. "all" shows every pin; "natives" narrows to state
  // natives; "keystones" narrows to the ~2% of species that support the
  // most caterpillars and pollinators (the "birds-and-bees powerhouses").
  // Filtering is client-side — every pin already carries the flags we
  // need, and recomputing the heatmap in the browser is cheap for the
  // volumes this map handles (<= 5k pins per state).
  const [filter, setFilter] = useState("all");
  const filteredPins = useMemo(() => {
    if (filter === "natives") return pins.filter((p) => p.is_native);
    if (filter === "keystones") return pins.filter((p) => p.is_keystone);
    return pins;
  }, [pins, filter]);
  const filteredHeatmap = useMemo(() => {
    // Rebuild heatmap buckets from filtered pins so the low-zoom view
    // reflects the current chip. Round bucket keys to 3 decimals to
    // match the server's own bucketing precision (0.05° grid).
    const buckets = new Map();
    for (const p of filteredPins) {
      const key = `${p.lat.toFixed(3)},${p.lon.toFixed(3)}`;
      const existing = buckets.get(key);
      if (existing) {
        existing.count += 1;
        if (p.is_native) existing.native_count += 1;
      } else {
        buckets.set(key, {
          lat: p.lat,
          lon: p.lon,
          count: 1,
          native_count: p.is_native ? 1 : 0,
        });
      }
    }
    return Array.from(buckets.values());
  }, [filteredPins]);

  return (
    <AppShell>
      <div className="md:max-w-5xl md:mx-auto px-5 pt-2 pb-6">
        <div className="flex items-baseline justify-between gap-3">
          <h1 className="font-display text-3xl text-leaf-ink leading-tight" data-testid="corridor-title">
            {t("corridor.title")}
          </h1>
          <span className="text-[11px] uppercase tracking-[0.16em] text-leaf-mist">
            {data?.state || user?.state}
          </span>
        </div>
        <p className="mt-1 text-sm text-leaf-mist max-w-2xl leading-relaxed">
          {t("corridor.subtitle")}
        </p>

        {/* Cross-promo — surfaces already-located scans that haven't been
            published yet, deep-linking into Gallery's bulk-publish flow. */}
        <PublishMoreCard />

        <div className="mt-4 grid grid-cols-3 gap-2 max-w-md">
          <StatChip
            icon={<MapPin className="w-4 h-4" strokeWidth={1.5} />}
            label={t("corridor.stats.pins")}
            value={totals.total}
            testid="corridor-stat-pins"
          />
          <StatChip
            icon={<Leaf className="w-4 h-4" strokeWidth={1.5} />}
            label={t("corridor.stats.natives")}
            value={totals.natives}
            testid="corridor-stat-natives"
          />
          <StatChip
            icon={<Flower className="w-4 h-4" strokeWidth={1.5} />}
            label={t("corridor.stats.keystones")}
            value={totals.keystones}
            testid="corridor-stat-keystones"
          />
        </div>

        {/* Filter chips — hidden while the corridor is empty (there's
            nothing to filter). Segmented control so state is visually
            unambiguous even at a glance; keyboard operable via native
            <button> semantics. */}
        {pins.length > 0 && (
          <div
            className="mt-3 inline-flex flex-wrap items-center gap-1.5 rounded-full bg-white/70 border border-[#E8E5DF] p-1"
            role="radiogroup"
            aria-label={t("corridor.filter.aria")}
            data-testid="corridor-filter-group"
          >
            <FilterChip
              active={filter === "all"}
              onClick={() => setFilter("all")}
              testid="corridor-filter-all"
              label={t("corridor.filter.all")}
              count={totals.total}
            />
            <FilterChip
              active={filter === "natives"}
              onClick={() => setFilter("natives")}
              testid="corridor-filter-natives"
              label={t("corridor.filter.natives")}
              count={totals.natives}
              icon={<Leaf className="w-3.5 h-3.5" strokeWidth={1.75} />}
            />
            <FilterChip
              active={filter === "keystones"}
              onClick={() => setFilter("keystones")}
              testid="corridor-filter-keystones"
              label={t("corridor.filter.keystones")}
              count={totals.keystones}
              icon={<Flower className="w-3.5 h-3.5" strokeWidth={1.75} />}
            />
          </div>
        )}

        <div
          className="mt-4 relative rounded-3xl overflow-hidden border border-[#E8E5DF] shadow-botanical bg-white"
          style={{ height: "min(70vh, 640px)" }}
          data-testid="corridor-map-container"
        >
          {loading ? (
            <div className="w-full h-full grid place-items-center text-leaf-mist text-sm">
              {t("corridor.loading")}
            </div>
          ) : err ? (
            <div className="w-full h-full grid place-items-center text-rust text-sm px-6 text-center">
              {err}
            </div>
          ) : pins.length === 0 ? (
            <EmptyState />
          ) : (
            <MapContainer
              center={[pins[0].lat, pins[0].lon]}
              zoom={STATE_DEFAULT_VIEW.zoom}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom
              data-testid="corridor-leaflet"
            >
              <MapA11y ariaLabel={t("corridor.mapAria")} />
              <TileLayer
                attribution="&copy; OpenStreetMap contributors"
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <ZoomWatcher onZoom={setZoom} />
              <FitBoundsOnce pins={filteredPins.length > 0 ? filteredPins : pins} />

              {/* Heatmap layer — bucketed circles sized by count. Visible at
                  low zoom so a whole state reads as green intensity. */}
              {!showPins &&
                filteredHeatmap.map((b) => (
                  <Circle
                    key={`h-${b.lat}-${b.lon}`}
                    center={[b.lat, b.lon]}
                    radius={800 + Math.log2(Math.max(1, b.count)) * 900}
                    pathOptions={{
                      color: "#3F6634",
                      fillColor: "#3F6634",
                      fillOpacity: 0.15 + Math.min(0.35, b.native_count / Math.max(1, b.count) * 0.35),
                      weight: 1,
                    }}
                  />
                ))}

              {/* Pin layer — individual anonymous markers. Native + keystone
                  markers are visually distinct so users spot special finds. */}
              {showPins &&
                filteredPins.map((p) => {
                  const label = [
                    p.plant_name || t("corridor.unknownPlant"),
                    p.is_native ? t("corridor.badges.native") : null,
                    p.is_keystone ? t("corridor.badges.keystone") : null,
                  ]
                    .filter(Boolean)
                    .join(", ");
                  return (
                    <CircleMarker
                      key={p.id}
                      center={[p.lat, p.lon]}
                      radius={p.is_keystone ? 9 : 6}
                      pathOptions={{
                        color: p.is_keystone ? "#B8860B" : p.is_native ? "#3F6634" : "#8B4513",
                        fillColor: p.is_keystone ? "#E9C46A" : p.is_native ? "#7BA05B" : "#C19A6B",
                        fillOpacity: 0.85,
                        weight: 2,
                      }}
                      eventHandlers={focusablePinHandlers(p, label)}
                    >
                      <Popup>
                        <div className="font-display text-base text-leaf-ink" data-testid="corridor-pin-name">
                          {p.plant_name || t("corridor.unknownPlant")}
                        </div>
                        {p.scientific_name && (
                          <div className="text-xs italic text-leaf-mist">{p.scientific_name}</div>
                        )}
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {p.is_native && (
                            <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-full bg-leaf/15 text-leaf-deep">
                              {t("corridor.badges.native")}
                            </span>
                          )}
                          {p.is_keystone && (
                            <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-full bg-[#E9C46A]/25 text-[#8a6a14]">
                              {t("corridor.badges.keystone")}
                            </span>
                          )}
                        </div>
                      </Popup>
                    </CircleMarker>
                  );
                })}
            </MapContainer>
          )}
          {/* Floating legend shortcut — anchored to the top-right of
              the map, above Leaflet's zoom controls (top-left) so they
              never overlap. Only rendered when there's data to explain
              — an empty map has nothing worth decoding. */}
          {pins.length > 0 && !loading && !err && (
            <MapLegendShortcut showPins={showPins} />
          )}
        </div>

        {/* Screen-reader-only equivalent of the pin cloud. Provides the
            plant identity + native/keystone badges as text so users on
            assistive tech aren't dependent on the mouse-only Leaflet
            popups (WCAG 1.1.1 Non-text Content). */}
        {pins.length > 0 && (
          <>
            <h2 className="sr-only" id="corridor-pin-list-heading">
              {t("corridor.pinListHeading")}
            </h2>
            <ul className="sr-only" aria-labelledby="corridor-pin-list-heading" data-testid="corridor-pin-list-sr">
              {filteredPins.map((p) => (
                <li key={`sr-${p.id}`}>
                  {p.plant_name || t("corridor.unknownPlant")}
                  {p.scientific_name ? ` (${p.scientific_name})` : ""}
                  {p.is_native ? `, ${t("corridor.badges.native")}` : ""}
                  {p.is_keystone ? `, ${t("corridor.badges.keystone")}` : ""}
                </li>
              ))}
            </ul>
          </>
        )}

        {/* Filter matched nothing — surface the reason inline instead of
            leaving the map silently empty. Also gives the user a one-tap
            escape back to the full view. */}
        {pins.length > 0 && filteredPins.length === 0 && (
          <div
            className="mt-3 flex items-center justify-between gap-3 rounded-2xl bg-[#FFF4DC] border border-[#E9C46A]/40 px-4 py-3"
            data-testid="corridor-filter-empty"
          >
            <p className="text-[13px] text-[#8a6a14]">
              {filter === "keystones"
                ? t("corridor.filter.emptyKeystones")
                : t("corridor.filter.emptyNatives")}
            </p>
            <button
              type="button"
              onClick={() => setFilter("all")}
              data-testid="corridor-filter-reset"
              className="shrink-0 text-xs rounded-full px-3 py-1.5 bg-leaf text-white hover:bg-leaf-deep transition"
            >
              {t("corridor.filter.showAll")}
            </button>
          </div>
        )}

        <div className="mt-4 flex items-start gap-2 rounded-2xl bg-parchment/60 border border-[#E8E5DF] px-4 py-3">
          <ShieldCheck className="w-4 h-4 mt-0.5 text-leaf shrink-0" strokeWidth={1.5} />
          <p className="text-[12px] text-leaf-mist leading-relaxed">
            {t("corridor.privacyNote")}
          </p>
        </div>

        {/* Legend — explains the map's visual encoding to first-time
            visitors so they can read the heatmap correctly. Content
            swaps with the map's current mode: at low zoom we're showing
            aggregate heat circles, at high zoom we're showing individual
            colored pins. Rendered only when there's data (otherwise
            there's nothing to interpret). */}
        {pins.length > 0 && (
          <div
            className="mt-3 rounded-2xl bg-white border border-[#E8E5DF] px-4 py-3"
            data-testid="corridor-legend"
            aria-label={t("corridor.legend.aria")}
          >
            {/* Header doubles as the collapse toggle. Uses aria-expanded
                + aria-controls so screen readers announce the open/close
                state correctly. */}
            <button
              type="button"
              onClick={toggleLegend}
              aria-expanded={legendOpen}
              aria-controls="corridor-legend-body"
              data-testid="corridor-legend-toggle"
              className="w-full flex items-center justify-between gap-2 -my-1 py-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf focus-visible:ring-offset-2 focus-visible:ring-offset-white rounded"
            >
              <span className="text-[10px] uppercase tracking-[0.16em] text-leaf-mist">
                {t("corridor.legend.heading")}
              </span>
              <ChevronDown
                className={`w-3.5 h-3.5 text-leaf-mist shrink-0 transition-transform ${
                  legendOpen ? "" : "-rotate-90"
                }`}
                strokeWidth={1.75}
                aria-hidden
              />
            </button>
            {legendOpen && (
              <div id="corridor-legend-body" className="mt-2" data-testid="corridor-legend-body">
                {showPins ? <PinLegend /> : <HeatmapLegend />}
              </div>
            )}
          </div>
        )}

        {pins.length > 0 && !showPins && (
          <div className="mt-3 flex items-center gap-2 text-[11px] text-leaf-mist" data-testid="corridor-zoom-hint">
            <Info className="w-3.5 h-3.5" strokeWidth={1.5} /> {t("corridor.zoomInHint")}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function StatChip({ icon, label, value, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E8E5DF] p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-leaf-mist">
        {icon}
        <span className="text-[10px] uppercase tracking-[0.14em]">{label}</span>
      </div>
      <div className="mt-1 font-display text-2xl text-leaf-ink" style={{ fontVariantNumeric: "tabular-nums lining-nums" }}>
        {value}
      </div>
    </div>
  );
}

/**
 * HeatmapLegend — explains the low-zoom aggregate view.
 *  • Green circle size = pin count in that bucket (bigger = more finds).
 *  • Green fill intensity = share of natives (denser green = more native
 *    plants concentrated there).
 * Kept short and inline so it fits under any narrow mobile map without
 * pushing the actual map further off-screen.
 */
function HeatmapLegend() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2" data-testid="corridor-legend-heatmap">
      <div className="flex items-center gap-3">
        <span aria-hidden className="inline-flex items-baseline gap-1">
          <span className="w-2.5 h-2.5 rounded-full bg-leaf/40" />
          <span className="w-4 h-4 rounded-full bg-leaf/40" />
          <span className="w-5 h-5 rounded-full bg-leaf/40" />
        </span>
        <span className="text-[12px] text-leaf-ink leading-tight">
          {t("corridor.legend.heatmapSize")}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <span aria-hidden className="inline-flex items-center gap-1">
          <span className="w-4 h-4 rounded-full" style={{ background: "rgba(63,102,52,0.20)" }} />
          <span className="w-4 h-4 rounded-full" style={{ background: "rgba(63,102,52,0.40)" }} />
          <span className="w-4 h-4 rounded-full" style={{ background: "rgba(63,102,52,0.60)" }} />
        </span>
        <span className="text-[12px] text-leaf-ink leading-tight">
          {t("corridor.legend.heatmapIntensity")}
        </span>
      </div>
    </div>
  );
}

/**
 * PinLegend — explains the high-zoom individual view. Swatches use the
 * exact same colors defined in the CircleMarker `pathOptions` above so
 * the key and the map stay in lockstep.
 */
function PinLegend() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="corridor-legend-pins">
      <LegendSwatch
        color="#7BA05B"
        ringColor="#3F6634"
        label={t("corridor.legend.pinNative")}
        testid="legend-swatch-native"
      />
      <LegendSwatch
        color="#E9C46A"
        ringColor="#B8860B"
        size={12}
        label={t("corridor.legend.pinKeystone")}
        testid="legend-swatch-keystone"
      />
      <LegendSwatch
        color="#C19A6B"
        ringColor="#8B4513"
        label={t("corridor.legend.pinNonNative")}
        testid="legend-swatch-nonnative"
      />
    </div>
  );
}

function LegendSwatch({ color, ringColor, size = 10, label, testid }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] text-leaf-ink" data-testid={testid}>
      <span
        aria-hidden
        className="inline-block rounded-full shrink-0"
        style={{
          width: size,
          height: size,
          background: color,
          boxShadow: `0 0 0 2px ${ringColor}`,
        }}
      />
      {label}
    </span>
  );
}

/**
 * MapLegendShortcut — floating "?" button at the top-right of the map
 * that opens a small popover with the same legend content. Complements
 * the below-map collapsible legend for users who have collapsed it or
 * who don't want to scroll to check the key.
 *
 * The button sits INSIDE the map's `overflow-hidden` container (so it
 * moves with the map on scroll), but the popover uses fixed positioning
 * anchored to the button's measured rect so it can escape the clip and
 * float freely over the map/page.
 */
function MapLegendShortcut({ showPins }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const [pos, setPos] = useState(null); // {top, right}

  // Position the popover just under the ? button, right-aligned so it
  // never runs off the right edge of the viewport on narrow screens.
  const recomputePosition = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({
      top: r.bottom + 8,
      right: Math.max(8, window.innerWidth - r.right),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    recomputePosition();
    const onScroll = () => recomputePosition();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, recomputePosition]);

  // Dismiss on outside click / Escape. Ignores clicks inside the popover
  // itself so the user can interact with any focusable child.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      const t2 = e.target;
      if (!(t2 instanceof Element)) return;
      if (t2.closest('[data-testid="map-legend-popover"]')) return;
      if (t2.closest('[data-testid="map-legend-shortcut"]')) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={t("corridor.legend.shortcutAria")}
        data-testid="map-legend-shortcut"
        // z-[500] beats Leaflet's default z of 400–800 for panes/controls
        // but stays under Leaflet's own popups (~700) which is a fine
        // priority: an open Leaflet pin popup is more important than our
        // help affordance.
        className="absolute top-3 right-3 z-[500] w-9 h-9 rounded-full bg-white/95 backdrop-blur border border-[#E8E5DF] text-leaf-ink shadow-sm flex items-center justify-center hover:bg-white transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf focus-visible:ring-offset-2 focus-visible:ring-offset-white"
      >
        <HelpCircle className="w-4 h-4" strokeWidth={1.75} aria-hidden />
      </button>

      {open && pos && (
        <div
          ref={popRef}
          role="dialog"
          aria-label={t("corridor.legend.aria")}
          data-testid="map-legend-popover"
          className="fixed z-[600] max-w-[300px] rounded-2xl bg-white border border-[#E8E5DF] shadow-botanical p-3"
          style={{ top: pos.top, right: pos.right }}
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <span className="text-[10px] uppercase tracking-[0.16em] text-leaf-mist">
              {t("corridor.legend.heading")}
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t("common.close")}
              data-testid="map-legend-popover-close"
              className="-mr-1 -mt-1 w-6 h-6 rounded-full text-leaf-mist hover:text-leaf-ink hover:bg-parchment flex items-center justify-center transition"
            >
              <XIcon className="w-3 h-3" strokeWidth={2} />
            </button>
          </div>
          {showPins ? <PinLegend /> : <HeatmapLegend />}
        </div>
      )}
    </>
  );
}

/**
 * FilterChip — segmented-control pill used above the map. Renders as a
 * native <button role="radio"> inside a role="radiogroup" so keyboard
 * users get standard radio semantics (Space toggles, arrow keys move
 * between chips via the browser's default focus order).
 */
function FilterChip({ active, onClick, label, count, icon, testid }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] uppercase tracking-[0.14em] font-medium transition-colors ${
        active
          ? "bg-leaf text-white shadow-sm"
          : "text-leaf-ink hover:bg-parchment"
      }`}
    >
      {icon}
      <span>{label}</span>
      <span
        className={`ml-0.5 text-[10px] tabular-nums ${active ? "text-white/85" : "text-leaf-mist"}`}
        aria-hidden="true"
      >
        {count}
      </span>
    </button>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className="h-full grid place-items-center px-6 text-center">
      <div>
        <div className="mx-auto w-16 h-16 rounded-full bg-leaf/10 flex items-center justify-center">
          <MapPin className="w-8 h-8 text-leaf" strokeWidth={1.5} />
        </div>
        {/* h2 (not h3) so /corridor descends h1 → h2 without a skip
            (WCAG 1.3.1 / axe heading-order best-practice). */}
        <h2 className="mt-3 font-display text-xl text-leaf-ink">{t("corridor.empty.title")}</h2>
        <p className="mt-2 text-sm text-leaf-mist max-w-sm mx-auto">{t("corridor.empty.message")}</p>
        <button
          type="button"
          onClick={() => navigate("/camera")}
          data-testid="corridor-empty-cta"
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-leaf hover:bg-leaf-deep text-white text-sm font-medium px-5 py-2.5 transition shadow-botanical"
        >
          <Camera className="w-4 h-4" strokeWidth={1.75} aria-hidden="true" />
          {t("corridor.empty.cta")}
        </button>
        <p className="mt-3 text-[11px] text-leaf-mist max-w-sm mx-auto">
          {t("corridor.empty.ctaHelp")}
        </p>
      </div>
    </div>
  );
}
